import React, { useEffect, useRef, useState } from "react";
import styles from "./ReportConfirmModal.module.css";
import { reactIcons } from "../../../icons/reactIcons";
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import Graphic from "@arcgis/core/Graphic";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import Point from "@arcgis/core/geometry/Point";
import { reverseGeocode } from "../../../utils/reverseGeocode";

interface ReportConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (coords: { lat: number; lng: number }) => void;
}

const ReportConfirmModal: React.FC<ReportConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [address, setAddress] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<MapView | null>(null);

  //  Detect location automatically
  const detectLocation = async () => {
    setLoading(true);

    try {
      if (!navigator.geolocation) {
        alert("Geolocation not supported by your browser");
        setLoading(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;

          // const lat = 38.245732;
          // const lng = -85.763582;

          setCoords({ lat, lng });

          // Fetch address using reverseGeocode utility
          const addr = await reverseGeocode(lat, lng);
          setAddress(addr);

          setLoading(false);
        },
        () => {
          alert("Unable to retrieve location");
          setLoading(false);
        },
      );
    } catch (error) {
      console.error("Location error:", error);
      alert("Error fetching location");
    } finally {
      setLoading(false);
    }
  };

  // Auto detect location when modal opens
  useEffect(() => {
    if (open) {
      detectLocation();
    }
  }, [open]);

  // Initialize ArcGIS Map
  useEffect(() => {
    if (!coords || !open) return;

    const webmap = new WebMap({
      basemap: "streets-navigation-vector",
    });

    const view = new MapView({
      container: mapRef.current as HTMLDivElement,
      map: webmap,
      center: [coords.lng, coords.lat],
      zoom: 16,
    });

    const point = new Point({
      longitude: coords.lng,
      latitude: coords.lat,
    });

    const markerSymbol = new SimpleMarkerSymbol({
      color: "#1a73e8",
      outline: {
        color: "#fff",
        width: 2,
      },
    });

    const pointGraphic = new Graphic({
      geometry: point,
      symbol: markerSymbol,
    });

    view.graphics.add(pointGraphic);
    viewRef.current = view;

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current.container = null;
      }
    };
  }, [coords, open]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("reportConfirm-modal-open");
    } else {
      document.body.classList.remove("reportConfirm-modal-open");
    }

    return () => document.body.classList.remove("reportConfirm-modal-open");
  }, [open]);

  if (!open) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <h3 className={styles.title}>Confirm Your Location</h3>
        <p className={styles.subtitle}>
          We've detected your current location. Please verify before continuing.
        </p>

        <div className={styles.locationCard}>
          <div className={styles.locationHeader}>
            <span>Your Current Location</span>
          </div>

          {coords ? (
            <>
              <div className={styles.address}>{address}</div>
              <div className={styles.coords}>
                {coords.lat.toFixed(4)}° N, {coords.lng.toFixed(4)}° E
              </div>

              <div ref={mapRef} className={styles.mapContainer}></div>
            </>
          ) : (
            <div className={styles.placeholder}>
              <p>
                {loading ? "Detecting your location..." : "Unable to detect"}
              </p>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button onClick={onClose} className={styles.cancelBtn}>
            Cancel
          </button>
          <button
            onClick={() => coords && onConfirm(coords)}
            className={styles.continueBtn}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportConfirmModal;
